import { FC, useEffect, useMemo, useState } from 'react'
import {
	Box,
	Button,
	ButtonDropdown,
	ColumnLayout,
	Container,
	ContentLayout,
	FormField,
	Header,
	SpaceBetween,
	Table
} from '@cloudscape-design/components'
import { useNavigate, useParams } from 'react-router-dom'
import { useOktaAuth } from '@okta/okta-react'

import useSingleCluster from '../../../hooks/query/useSingleCluster'
import useSingleTags from '../../../hooks/query/useSingleTags'
import useSingleAccount from '../../../hooks/query/useSingleAccount'
import useAccounts from '../../../hooks/query/useAccounts'
import useTagsByGCPProjectId from '../../../hooks/query/useTagsByGCPProjectId'
import { applicationFromWorkloadClusterRepoName, resolveClusterPortfolioPrefix } from '../../../helper/clusterWorkloadRepo'
import LoadingSpinner from '../../../components/LoadingSpinner'
import type { IpAddressAllocation } from '../../../types/cluster'
import type { Account } from '../../../types/account'
import { getClusterNetworkFromAccount } from '../../../helper/clusterAccountNetwork'
import HeaderWithStatusBadges, {
	type Approval,
	mapPageStatusToApprovalStatus
} from '../../../components/Common/HeaderWithStatusBadges'
import ClusterApprovalForm from '../../../components/Cluster/ApprovalForm'
import ClusterRejectionForm from '../../../components/Cluster/RejectionForm'
import ReadGenericTags from '../../../components/ControlledFormInputs/GenericTagsRead'
import RequiredPermissions from '../../../helper/RequiredPermissions'
import CONSTANTS from '../../../constants'
import type { TagsApproval } from '../../../types/tags'

const ClusterDetails: FC = () => {
	const { id: clusterId } = useParams() as { id: string }
	const navigate = useNavigate()
	const { authState } = useOktaAuth()
	const currentUserEmail = (authState?.idToken?.claims?.email as string) ?? ''

	const { data: cluster, isLoading } = useSingleCluster(clusterId)
	const { data: tags } = useSingleTags(cluster?.tagsID ?? '', {
		enabled: !!cluster?.tagsID
	})
	const distributionList = String(
		cluster?.details?.distributionList ?? ''
	).trim()

	const { data: accounts } = useAccounts()

	const linkedAccountByDistributionList =
		useMemo(() => {
			if (
				!distributionList ||
				!Array.isArray(accounts)
			) {
				return undefined
			}

			const target =
				distributionList.toLowerCase()

			return accounts.find(
				(item) =>
					String(
						item?.details
							?.distributionList ??
							''
					)
						.toLowerCase()
						.trim() === target
			)
		}, [accounts, distributionList])

	const linkedAccountTagsId = String(
		linkedAccountByDistributionList?.tagsID ??
		''
	).trim()

	const { data: accountTags } =
		useSingleTags(linkedAccountTagsId, {
			enabled: Boolean(linkedAccountTagsId)
		})

	const { data: accountWithTags } = useSingleAccount(
		distributionList,
		true,
		{ enabled: Boolean(distributionList) }
	)

	const gcpProjectRef = useMemo(() => {
		const details =
			(cluster?.details ?? {}) as Record<
				string,
				unknown
			>

		const candidates = [
			details.gcpProjectID,
			details.gcpProjectId,
			details.gcpProjectName,
			details.projectName
		]

		for (const candidate of candidates) {
			const value = String(
				candidate ?? ''
			).trim()

			if (value) {
				return value
			}
		}

		return ''
	}, [cluster?.details])

	const { data: gcpProjectTags } =
		useTagsByGCPProjectId(
			gcpProjectRef,
			{ enabled: Boolean(gcpProjectRef) }
		)

	const getApprovalStatusList = <T extends { approvalStatus?: TagsApproval[] } | undefined>(
		value: T
	): TagsApproval[] => {
		if (!value || !Array.isArray(value.approvalStatus)) {
			return []
		}

		return value.approvalStatus
	}

	const effectiveApprovalStatus =
		useMemo<TagsApproval[]>(() => {
			const accountTagsApprovals = getApprovalStatusList(
				accountTags as { approvalStatus?: TagsApproval[] } | undefined
			)

			const accountApprovals = getApprovalStatusList(
				(accountWithTags?.tags as { approvalStatus?: TagsApproval[] } | undefined)
			)

			const gcpProjectApprovals = getApprovalStatusList(
				(gcpProjectTags as { approvalStatus?: TagsApproval[] } | undefined)
			)

			if (gcpProjectRef && gcpProjectApprovals.length > 0) {
				return gcpProjectApprovals
			}

			if (accountApprovals.length > 0) {
				return accountApprovals
			}

			if (accountTagsApprovals.length > 0) {
				return accountTagsApprovals
			}

			return getApprovalStatusList(
				tags as { approvalStatus?: TagsApproval[] } | undefined
			)
		}, [
			accountTags,
			accountWithTags?.tags,
			gcpProjectRef,
			gcpProjectTags,
			tags
		])

	const { data: account } = useSingleAccount(
		cluster?.details?.distributionList ?? '',
		false
	)

	const [showApprovalForm, setShowApprovalForm] = useState(false)
	const [showRejectionForm, setShowRejectionForm] = useState(false)
	const [workloadClusterVersion, setWorkloadClusterVersion] = useState('')

	const defaultSubnetAliases = useMemo(
		() => [
			{
				id: '1',
				alias: 'eks',
				identifier: 'tag:Purpose',
				value: 'WORKERNODE'
			},
			{
				id: '2',
				alias: 'shared',
				identifier: 'tag:Purpose',
				value: 'SHARED-WORKERNODE'
			}
		],
		[]
	)

	const [subnetAliasesItems, setSubnetAliasesItems] = useState<
		Array<{ id: string; alias: string; identifier: string; value: string }>
	>(defaultSubnetAliases)

	useEffect(() => {
		if (
			!cluster ||
			String(cluster.status).toUpperCase() !== 'APPROVED' ||
			!cluster.approval
		) {
			return
		}

		if (cluster.approval.workloadClusterVersion != null) {
			setWorkloadClusterVersion(String(cluster.approval.workloadClusterVersion))
		}

		if (
			cluster.approval.subnetAliases &&
			Array.isArray(cluster.approval.subnetAliases) &&
			cluster.approval.subnetAliases.length > 0
		) {
			setSubnetAliasesItems(
				cluster.approval.subnetAliases.map((a, i) => ({
					id: String(i + 1),
					alias: a.alias ?? '',
					identifier: a.identifier ?? '',
					value: a.value ?? ''
				}))
			)
		}
	}, [cluster?.clusterID, cluster?.status, cluster?.approval, cluster])

	const { portfolioPrefix, application, environment, accountId } = useMemo(() => {
		const env = cluster?.details?.environment ?? ''
		const accId = cluster?.details?.accountID ?? ''
		const tagDetails = (tags?.details ?? {}) as Record<string, unknown>
		const appFromTags = tagDetails.systemAlias ?? tagDetails.system_alias

		const appStr =
			cluster?.details?.application ??
			(typeof appFromTags === 'object' &&
			appFromTags !== null &&
			'value' in appFromTags
				? String((appFromTags as { value: string }).value)
				: String(
						appFromTags ??
							applicationFromWorkloadClusterRepoName(cluster?.details?.clusterName) ??
							''
				  ))

		const portStr = resolveClusterPortfolioPrefix({
			portfolio: cluster?.details?.portfolio,
			tagProject: tagDetails.tagProject
		})

		return {
			portfolioPrefix: portStr,
			application: String(appStr ?? ''),
			environment: env,
			accountId: accId
		}
	}, [cluster?.details, tags?.details])

	const isAllTagsApproved = useMemo(() => {
		const statuses =
			effectiveApprovalStatus

		if (!statuses || !Array.isArray(statuses) || statuses.length === 0) {
			return true
		}

		return statuses.every((t) => t.status === 1)
	}, [effectiveApprovalStatus])

	const clusterHeaderApprovals = useMemo<Approval[]>(() => {
		const approverStatuses =
			effectiveApprovalStatus

		const normalizeApproverName = (value: unknown): string =>
			String(value ?? '')
				.toLowerCase()
				.replace(/[^a-z0-9]/g, '')

		const normalizeApprovalStatus = (
			status: unknown
		): Approval['status'] => {
			if (
				status === 1 ||
				status === '1' ||
				status === true ||
				String(status).toLowerCase() === 'approved' ||
				String(status).toLowerCase() === 'true'
			) {
				return 'APPROVED'
			}

			if (
				status === 0 ||
				status === '0' ||
				status === false ||
				String(status).toLowerCase() === 'rejected' ||
				String(status).toLowerCase() === 'false'
			) {
				return 'REJECTED'
			}

			return 'PENDING'
		}

		const getStatusForName = (name: string): Approval['status'] => {
			const normalizedTarget = normalizeApproverName(name)
			const matches = approverStatuses.filter(
				(approval: TagsApproval) =>
					normalizeApproverName(
						approval.name
					) === normalizedTarget
			)

			if (matches.length === 0) {
				return 'PENDING'
			}

			const resolvedStatuses = matches.map((approval) =>
				normalizeApprovalStatus(approval.status)
			)

			if (resolvedStatuses.includes('REJECTED')) {
				return 'REJECTED'
			}

			if (resolvedStatuses.includes('APPROVED')) {
				return 'APPROVED'
			}

			return 'PENDING'
		}

		const pageStatus = mapPageStatusToApprovalStatus(cluster?.status)

		const cloudTribeStatus: Approval['status'] =
			getStatusForName('CloudTribe') !== 'PENDING'
				? getStatusForName('CloudTribe')
				: pageStatus && pageStatus !== 'PENDING'
					? pageStatus
					: 'PENDING'

		return [
			{
				label: 'Platform Owner',
				status: getStatusForName('PlatformOwner')
			},
			{
				label: 'Finance',
				status: getStatusForName('FinanceTeam')
			},
			{
				label: 'Cloud Eco',
				status: getStatusForName('CloudEconomicsTeam')
			},
			{
				label: 'Cloud Tribe',
				status: cloudTribeStatus
			}
		]
	}, [cluster?.status, effectiveApprovalStatus])

	const { vpcName: vpcValue, privateHostedZoneName: dnsZoneApex } = useMemo(
		() =>
			getClusterNetworkFromAccount(
				account as Account | null | undefined,
				environment,
				application,
				{
					portfolioPrefix,
					systemAlias: application,
					distributionList: cluster?.details?.distributionList,
					accountType: account?.type,
					isStgFlow: environment === 'st'
				}
			),
		[account, environment, portfolioPrefix, application, cluster?.details?.distributionList]
	)

	const defaultSg1 = portfolioPrefix
		? `${portfolioPrefix}-${application}-${environment}-default-sg-1`
		: `${application}-${environment}-default-sg-1`
	const defaultSg2 = portfolioPrefix
		? `${portfolioPrefix}-${application}-${environment}-default-sg-2`
		: `${application}-${environment}-default-sg-2`

	const tfProvidersItems = useMemo(
		() => [
			{
				id: '1',
				alias: 'environment',
				region: 'ap-southeast-1',
				arn: `arn:aws:iam::${accountId}:role/AC2-IRS-DO-GLI-BL-${accountId}-STG`,
				externalId: `GitlabRunner-426956141294-${accountId}`
			},
			{
				id: '2',
				alias: 'route53',
				region: 'ap-southeast-1',
				arn: `arn:aws:iam::${accountId}:role/AC2-IRS-DO-GLI-BL-${accountId}-STG`,
				externalId: `GitlabRunner-426956141294-${accountId}`
			},
			{
				id: '3',
				alias: 'dr',
				region: 'ap-southeast-2',
				arn: `arn:aws:iam::${accountId}:role/AC2-IRS-DO-GLI-BL-${accountId}-STG`,
				externalId: `GitlabRunner-426956141294-${accountId}`
			},
			{
				id: '4',
				alias: 'security',
				region: 'ap-southeast-1',
				arn: 'arn:aws:iam::085094121557:role/AC2-IRS-DO-GLD-SECURITY-STG',
				externalId: 'atlas'
			},
			{
				id: '5',
				alias: 'security_dr',
				region: 'ap-southeast-2',
				arn: 'arn:aws:iam::085094121557:role/AC2-IRS-DO-GLD-SECURITY-STG',
				externalId: 'atlas'
			},
			{
				id: '6',
				alias: 'sharedservices',
				region: 'ap-southeast-1',
				arn: 'arn:aws:iam::337994588368:role/AC2-IRS-DO-GLD-ATLAS_SHARED_SERVICES-PRD',
				externalId: 'atlas'
			}
		],
		[accountId]
	)

	const approvedSectionSubnetAliases = useMemo(() => {
		const fromApproval = cluster?.approval?.subnetAliases

		if (fromApproval && Array.isArray(fromApproval) && fromApproval.length > 0) {
			return fromApproval.map((a, i) => ({
				id: String(i + 1),
				alias: a.alias ?? '',
				identifier: a.identifier ?? '',
				value: a.value ?? ''
			}))
		}

		return subnetAliasesItems
	}, [cluster?.approval?.subnetAliases, subnetAliasesItems])

	const approvedSectionTfProviders = useMemo(() => {
		const fromApproval = cluster?.approval?.tfProviders

		if (fromApproval && Array.isArray(fromApproval) && fromApproval.length > 0) {
			return fromApproval.map((p, i) => ({
				id: String(i + 1),
				alias: p.alias ?? '',
				region: p.region ?? '',
				arn: p.arn ?? '',
				externalId: p.externalId ?? ''
			}))
		}

		return tfProvidersItems
	}, [cluster?.approval?.tfProviders, tfProvidersItems])

	const approvedSectionSecurityGroups = useMemo(() => {
		const fromApproval = cluster?.approval?.securityGroups

		if (fromApproval && Array.isArray(fromApproval) && fromApproval.length > 0) {
			return fromApproval
		}

		return [defaultSg1, defaultSg2]
	}, [cluster?.approval?.securityGroups, defaultSg1, defaultSg2])

	const approvedWorkloadVersion =
		cluster?.approval?.workloadClusterVersion != null &&
		cluster.approval.workloadClusterVersion !== ''
			? String(cluster.approval.workloadClusterVersion)
			: workloadClusterVersion

	const approvedIpAllocations = useMemo(() => {
		const fromApproval = cluster?.approval?.ip_address_allocations

		if (fromApproval && Array.isArray(fromApproval) && fromApproval.length > 0) {
			return fromApproval
		}

		return cluster?.details?.ip_address_allocations ?? []
	}, [
		cluster?.approval?.ip_address_allocations,
		cluster?.details?.ip_address_allocations
	])

	const isProductionEnv =
		(cluster?.details?.environment ?? '').toLowerCase() === 'pd'

	const approvedIpAllocationColumns = useMemo(
		() => [
			{
				id: 'label',
				header: 'Label',
				cell: (e: IpAddressAllocation) => e.label
			},
			{
				id: 'cidrAzA',
				header: 'CIDR AZ-A',
				cell: (e: IpAddressAllocation) => e.cidrAzA || '—'
			},
			{
				id: 'cidrAzB',
				header: 'CIDR AZ-B',
				cell: (e: IpAddressAllocation) => e.cidrAzB || '—'
			},
			...(isProductionEnv
				? [
						{
							id: 'cidrAzC',
							header: 'CIDR AZ-C',
							cell: (e: IpAddressAllocation) =>
								(e.cidrAzC ?? '') || '—'
						}
				  ]
				: [])
		],
		[isProductionEnv]
	)

	if (isLoading) return <LoadingSpinner />

	if (!cluster) {
		return (
			<Container>
				<Header variant="h2">Cluster not found</Header>
				<Button onClick={() => navigate('/clusters')}>Back to Clusters</Button>
			</Container>
		)
	}

	const fields = [
		{ label: 'Application', value: cluster.details?.application ?? '-' },
		{
			label: 'Distribution List',
			value: cluster.details?.distributionList ?? '-'
		},
		{ label: 'Account ID', value: cluster.details?.accountID ?? '-' },
		{ label: 'Environment', value: cluster.details?.environment ?? '-' },
		{
			label: 'Workload Cluster Repo',
			value: cluster.details?.clusterName ?? '-'
		},
		{ label: 'Requested By', value: cluster.requestorID ?? '-' },
		{
			label: 'Created At',
			value: cluster.creationTime
				? new Date(cluster.creationTime).toLocaleDateString()
				: '-'
		}
	]

	const ipAllocations = cluster.details?.ip_address_allocations ?? []
	const addonsConfig = cluster.details?.aws_eks_custom_addons_config as
		| Record<string, boolean>
		| undefined

	const EKS_ADDONS_LIST: Array<{ key: string; label: string }> = [
		{
			key: 'aws_load_balancer_controller',
			label: 'AWS Load Balancer Controller'
		},
		{ key: 'karpenter', label: 'Karpenter' },
		{ key: 'metrics_server', label: 'Metrics Server' },
		{ key: 'external_secrets', label: 'External Secrets' },
		{ key: 'argo_rollouts', label: 'Argo Rollouts' },
		{ key: 'argo_workflows', label: 'Argo Workflows' },
		{ key: 'calico', label: 'Calico' },
		{ key: 'cert_manager', label: 'Cert Manager' },
		{ key: 'cluster_autoscaler', label: 'Cluster Autoscaler' },
		{ key: 'external_dns', label: 'External DNS' },
		{ key: 'kubecost', label: 'Kubecost' },
		{ key: 'kyverno', label: 'Kyverno' },
		{ key: 'falcon_sensor', label: 'Falcon Sensor' },
		{ key: 'keda', label: 'KEDA' }
	]

	const selectedAddons = addonsConfig
		? EKS_ADDONS_LIST.filter((addon) => addonsConfig[addon.key] === true)
		: []

	return (
		<ContentLayout
			header={
				<Box>
					<HeaderWithStatusBadges
						headerName={
							cluster.details?.clusterName ?? cluster.clusterID
						}
						status={cluster.status}
						approvals={clusterHeaderApprovals}
						approvalStatus={effectiveApprovalStatus}
						navigateTo="/clusters"
					/>
				</Box>
			}
		>
			<SpaceBetween direction="vertical" size="l">
						<Container header={<Header variant="h3">Cluster Details</Header>}>
							<SpaceBetween direction="vertical" size="l">
								<ColumnLayout columns={3} variant="text-grid">
									{fields.map(({ label, value }) => (
										<div key={label}>
											<Box variant="awsui-key-label">{label}</Box>
											<div>{value}</div>
										</div>
									))}
								</ColumnLayout>

								{ipAllocations.length > 0 && (
									<FormField label="IP Address Allocation">
										<Table
											columnDefinitions={approvedIpAllocationColumns}
											items={ipAllocations}
											empty="No allocations"
										/>
									</FormField>
								)}

								{selectedAddons.length > 0 && (
									<FormField label="AWS EKS add-ons">
										<Box variant="p">
											{selectedAddons.map((a) => a.label).join(', ')}
										</Box>
									</FormField>
								)}
							</SpaceBetween>
						</Container>

						{cluster.tagsID && (
							<Container header={<Header variant="h3">Tags</Header>}>
								<ReadGenericTags
									tagsId={cluster.tagsID}
									isApprovalDisabled={
										String(cluster.status).toUpperCase() !==
										'PENDING_APPROVAL'
									}
								/>
							</Container>
						)}

						{String(cluster.status).toUpperCase() === 'PENDING_APPROVAL' &&
							currentUserEmail &&
							currentUserEmail !== cluster.requestorID &&
							!showApprovalForm &&
							!showRejectionForm && (
								<RequiredPermissions
									requiredPermissions={CONSTANTS.CLUSTER_APPROVER}
								>
									<Box float="right">
										<ButtonDropdown
											items={
												isAllTagsApproved
													? [
															{
																text: 'To Approve...',
																id: 'to-approve'
															},
															{
																text: 'To Reject...',
																id: 'to-reject'
															}
													  ]
													: [
															{
																text: 'To Reject...',
																id: 'to-reject'
															}
													  ]
											}
											onItemClick={({ detail }) => {
												if (detail.id === 'to-approve') {
													setShowApprovalForm(true)
													setShowRejectionForm(false)
												}

												if (detail.id === 'to-reject') {
													setShowApprovalForm(false)
													setShowRejectionForm(true)
												}
											}}
											variant="primary"
										>
											Action
										</ButtonDropdown>
									</Box>
								</RequiredPermissions>
							)}

						{String(cluster.status).toUpperCase() === 'PENDING_APPROVAL' &&
							showApprovalForm && (
								<RequiredPermissions
									requiredPermissions={CONSTANTS.CLUSTER_APPROVER}
								>
									<ClusterApprovalForm
										cluster={cluster}
										shouldShowApprovalForm={setShowApprovalForm}
									/>
								</RequiredPermissions>
							)}

						{String(cluster.status).toUpperCase() === 'PENDING_APPROVAL' &&
							showRejectionForm && (
								<RequiredPermissions
									requiredPermissions={CONSTANTS.CLUSTER_APPROVER}
								>
									<ClusterRejectionForm
										cluster={cluster}
										shouldShowRejectionForm={setShowRejectionForm}
									/>
								</RequiredPermissions>
							)}

						{String(cluster.status).toUpperCase() === 'APPROVED' && (
							<>
								<Container
									header={<Header variant="h3">Cluster Approved Details</Header>}
								>
									<SpaceBetween direction="vertical" size="l">
										<FormField label="VPC Aliases">
											<ColumnLayout columns={2} variant="text-grid">
												<div>
													<Box variant="awsui-key-label">VPC Identifier</Box>
													<Box variant="p">tag:Name</Box>
												</div>

												<div>
													<Box variant="awsui-key-label">VPC Value</Box>
													<Box variant="p">{vpcValue}</Box>
												</div>
											</ColumnLayout>
										</FormField>

										<FormField label="DNS Zone Apex">
											<Box variant="p">{dnsZoneApex}</Box>
										</FormField>

										<FormField label="Default Security Groups">
											<SpaceBetween direction="vertical" size="s">
												{approvedSectionSecurityGroups.map((sg, idx) => (
													<Box key={`sg-${idx}`} variant="p">
														{sg}
													</Box>
												))}
											</SpaceBetween>
										</FormField>

										<FormField label="Subnet Aliases">
											<Table
												columnDefinitions={[
													{
														id: 'alias',
														header: 'Subnet Alias',
														cell: (e) => e.alias
													},
													{
														id: 'identifier',
														header: 'Identifier',
														cell: (e) => e.identifier
													},
													{
														id: 'value',
														header: 'Value',
														cell: (e) => e.value
													}
												]}
												items={approvedSectionSubnetAliases}
												empty="No items"
											/>
										</FormField>

										{approvedIpAllocations.length > 0 && (
											<FormField label="IP Address Allocation">
												<Table
													columnDefinitions={approvedIpAllocationColumns}
													items={approvedIpAllocations}
													empty="No allocations"
												/>
											</FormField>
										)}
									</SpaceBetween>
								</Container>

								<Container header={<Header variant="h3">TF Providers</Header>}>
									<Table
										columnDefinitions={[
											{
												id: 'alias',
												header: 'Alias',
												cell: (e) => e.alias
											},
											{
												id: 'region',
												header: 'Region',
												cell: (e) => e.region
											},
											{
												id: 'arn',
												header: 'ARN',
												cell: (e) => e.arn
											},
											{
												id: 'externalId',
												header: 'External ID',
												cell: (e) => e.externalId
											}
										]}
										items={approvedSectionTfProviders}
										empty="No providers"
									/>
								</Container>

								<Container
									header={<Header variant="h3">Workload Cluster Version</Header>}
								>
									<FormField description="AWS EKS Kubernetes version" label="">
										<Box variant="p">{approvedWorkloadVersion || '—'}</Box>
									</FormField>
								</Container>
							</>
						)}

						<Box float="right">
							<Button variant="link" onClick={() => navigate('/clusters')}>
								Back to Clusters
							</Button>
						</Box>
					</SpaceBetween>
		</ContentLayout>
	)
}

export default ClusterDetails